import React, { useState, useEffect, useReducer } from 'react';

import { useSelector } from 'react-redux';
import { reduxRootTypes } from 'src/types';

import { reducer } from './toggleReducer';
import { State } from './kanban.config';
import { ColumnKey } from './kanban.config';

import './Kanban.scss';
import { BsArrowBarLeft, BsArrowBarRight } from 'react-icons/bs';
import { FaFilterCircleXmark } from 'react-icons/fa6';

import { TaskTableResultType } from 'src/types/services/ppm/task-service.type';
import { WorkPackageResultType } from 'src/types/services/ppm/work-package-service.type';
import { TestTableResultType } from 'src/types/services/ppm/test-service.type';

import DraggableModal from '../DraggableModal/DraggableModal';
import { KanbanFilterOptions } from './kanban.config';
import { ProjectInputTableResultType } from 'src/types/services/ppm/project-input-service.type';
import { DeliverableTableResultType } from 'src/types/services/ppm/deliverable-service.type';
import { KanbanTableResultType } from 'src/types/services/ppm/kanban-service.type';
import { forEach, set } from 'lodash';
import { ProjectCreateResultType } from 'src/types/services/ppm/project-service.type';
import { useTranslation } from 'react-i18next';
import Row from 'antd/es/grid/row';
import Col from 'antd/es/grid/col';
import Card from 'antd/es/card';

const Kanban: React.FC<KanbanFilterOptions> = ({
  workPackageId,
  taskId,
  projectInputId,
  deliverableId,
  testId,
  ccrId,
  ttId,
  projectId,
  setOnClickOpen,
}) => {
  const { t } = useTranslation();

  const eventState = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);

  const workPackageList = useSelector((state: reduxRootTypes.RootStateType) => state.workPackageState.workPackageList);
  const taskList = useSelector((state: reduxRootTypes.RootStateType) => state.taskState.taskList);
  const projectsList = useSelector((state: reduxRootTypes.RootStateType) => state.projectsState.projectsList);
  const projectInputList = useSelector(
    (state: reduxRootTypes.RootStateType) => state.projectInputState.projectInputList,
  );
  const kanbanList = useSelector((state: reduxRootTypes.RootStateType) => state.kanbanState.kanbanList);
  const deliverableList = useSelector((state: reduxRootTypes.RootStateType) => state.deliverableState.deliverableList);

  const testList = useSelector((state: reduxRootTypes.RootStateType) => state.testState.testList);
  const lineOfEffortList = useSelector(
    (state: reduxRootTypes.RootStateType) => state.lineOfEffortState.lineOfEffortList,
  );

  const [filteredWorkPackageList, setFilteredWorkPackageList] = useState<WorkPackageResultType[]>(
    ccrId || ttId ? [] : workPackageList,
  );
  const [filteredProjectsList, setFilteredProjectsList] = useState<ProjectCreateResultType[]>(
    ccrId || ttId ? [] : projectsList,
  );
  const [filteredTaskList, setFilteredTaskList] = useState<TaskTableResultType[]>(ccrId || ttId ? [] : taskList);
  const [filteredProjectInputList, setFilteredProjectInputList] = useState<ProjectInputTableResultType[]>(
    ccrId || ttId ? [] : projectInputList,
  );
  const [filteredKanbanList, setFilteredKanbanList] = useState<KanbanTableResultType[]>(
    ccrId || ttId ? [] : kanbanList,
  );
  const [filteredDeliverableList, setFilteredDeliverableList] = useState<DeliverableTableResultType[]>(
    ccrId || ttId ? [] : deliverableList,
  );
  const [filteredTestList, setFilteredTestList] = useState<TestTableResultType[]>(ccrId || ttId ? [] : testList);

  const [projectPermanentHover, setProjectPermanentHover] = useState<string | null>(null);
  const [workPackagePermanentHover, setWorkPackagePermanentHover] = useState<string | null>(null);
  const [taskPermanentHover, setTaskPermanentHover] = useState<string | null>(null);
  const [projectInputPermanentHover, setProjectInputPermanentHover] = useState<string | null>(null);
  const [deliverablePermanentHover, setDeliverablePermanentHover] = useState<string | null>(null);
  const [testPermanentHover, setTestPermanentHover] = useState<string | null>(null);

  useEffect(() => {
    setFilteredKanbanList(kanbanList);
  }, [kanbanList]);

  useEffect(() => {
    if (projectId) {
      handleProjectsFilter(projectId);
    }

    if (workPackageId) {
      handleWorkPackageFilter(workPackageId);
    }

    if (taskId) {
      handleTaskFilter(taskId);
    }

    if (projectInputId) {
      handleProjectInputsFilter(projectInputId);
    }

    if (deliverableId) {
      handleDeliverableFilter(deliverableId);
    }

    if (testId) {
      handleDeliverableFilter(testId);
    }
  }, [workPackageId, taskId, projectInputId, deliverableId, testId]);

  const handleCcrTaskFilter = (taskFilterResult: TaskTableResultType[]) => {
    setFilteredTaskList(taskFilterResult);

    const workPackageFilterResult = workPackageList.filter((workPackageObj) =>
      taskFilterResult.some((taskObj) => taskObj.workPackageNumberId === workPackageObj.id),
    );
    setFilteredWorkPackageList(workPackageFilterResult);

    const relatedProjectsList = projectsList.filter((projectObj) =>
      workPackageFilterResult.some((workPackageObj) => workPackageObj.projectId === projectObj.id),
    );
    setFilteredProjectsList(relatedProjectsList);

    const relatedProjectInputList = projectInputList.filter((projectInputObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === projectInputObj.taskNumberId),
    );
    setFilteredProjectInputList(relatedProjectInputList);

    const relatedKanbanList = kanbanList.filter((actionObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === actionObj.taskNumberId),
    );
    setFilteredKanbanList(relatedKanbanList);

    const relatedDeliverableList = deliverableList.filter((outcomeObj) =>
      taskFilterResult.some((actionObj) => actionObj.id === outcomeObj.taskNumberId),
    );
    setFilteredDeliverableList(relatedDeliverableList);

    const relatedTestList = testList.filter((observationTaskObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === observationTaskObj.taskNumberId),
    );
    setFilteredTestList(relatedTestList);
  };

  const handleTtTaskFilter = (taskFilterResult: TaskTableResultType[]) => {
    setFilteredTaskList(taskFilterResult);

    const workPackageFilterResult = workPackageList.filter((workPackageObj) =>
      taskFilterResult.some((taskObj) => taskObj.workPackageNumberId === workPackageObj.id),
    );
    setFilteredWorkPackageList(workPackageFilterResult);

    const relatedProjectsList = projectsList.filter((projectObj) =>
      workPackageFilterResult.some((workPackageObj) => workPackageObj.projectId === projectObj.id),
    );
    setFilteredProjectsList(relatedProjectsList);

    const relatedProjectInputList = projectInputList.filter((projectInputObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === projectInputObj.taskNumberId),
    );
    setFilteredProjectInputList(relatedProjectInputList);

    const relatedKanbanList = kanbanList.filter((actionObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === actionObj.taskNumberId),
    );
    setFilteredKanbanList(relatedKanbanList);

    const relatedDeliverableList = deliverableList.filter((outcomeObj) =>
      taskFilterResult.some((actionObj) => actionObj.id === outcomeObj.taskNumberId),
    );
    setFilteredDeliverableList(relatedDeliverableList);

    const relatedTestList = testList.filter((observationTaskObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === observationTaskObj.taskNumberId),
    );
    setFilteredTestList(relatedTestList);
  };

  useEffect(() => {
    if (ccrId) {
      const ccrTaskIdList: TaskTableResultType[] = [];

      forEach(taskList, (task) => {
        if (task.primaryRequirementId === ccrId) {
          ccrTaskIdList.push(task);
        }
      });

      handleCcrTaskFilter(ccrTaskIdList);
    } else if (ttId) {
      const ttRelatedTasks: TaskTableResultType[] = [];

      forEach(taskList, (task) => {
        if (task.primaryRequirementId === ttId) {
          ttRelatedTasks.push(task);
        }
      });

      handleTtTaskFilter(ttRelatedTasks);
    }
  }, [ccrId, ttId]);

  const toggleInitialState: State = {
    collapsed: {
      project: false,
      workPackage: false,
      task: false,
      projectInputs: false,
      kanban: false,
      deliverable: false,
      test: false,
    },
  };

  const [toggleState, dispatch] = useReducer(reducer, toggleInitialState);

  const toggleCollapse = (column: ColumnKey) => {
    dispatch({ type: 'TOGGLE_COLLAPSE', column });
  };

  const handleProjectsFilter = (id: string) => {
    setTaskPermanentHover(null);
    setProjectInputPermanentHover(null);
    setDeliverablePermanentHover(null);
    setTestPermanentHover(null);
    setWorkPackagePermanentHover(null);

    toggleState.collapsed.project = false;

    setProjectPermanentHover(id);

    const projectFilterResult = projectsList.filter((project) => project.id === id);
    setFilteredProjectsList(projectFilterResult);

    const relatedWorkPackageList = workPackageList.filter((workPackage) => workPackage.projectId === id);
    setFilteredWorkPackageList(relatedWorkPackageList);

    const relatedTaskList = taskList.filter((task) =>
      relatedWorkPackageList.some((workPackage) => workPackage.id === task.workPackageNumberId),
    );
    setFilteredTaskList(relatedTaskList);

    const relatedProjectInputList = projectInputList.filter((projectInput) =>
      relatedTaskList.some((task) => task.id === projectInput.taskNumberId),
    );
    setFilteredProjectInputList(relatedProjectInputList);

    const relatedKanbanList = kanbanList.filter((kanban) =>
      relatedTaskList.some((task) => task.id === kanban.taskNumberId),
    );
    setFilteredKanbanList(relatedKanbanList);

    const relatedDeliverableList = deliverableList.filter((deliverable) =>
      relatedTaskList.some((task) => task.id === deliverable.taskNumberId),
    );
    setFilteredDeliverableList(relatedDeliverableList);

    const relatedTestList = testList.filter((test) => relatedTaskList.some((task) => task.id === test.taskNumberId));
    setFilteredTestList(relatedTestList);
  };

  const handleWorkPackageFilter = (id: string) => {
    setTaskPermanentHover(null);
    setProjectInputPermanentHover(null);
    setDeliverablePermanentHover(null);
    setTestPermanentHover(null);

    toggleState.collapsed.workPackage = false;
    toggleState.collapsed.projectInputs = true;
    toggleState.collapsed.project = true;

    setWorkPackagePermanentHover(id);

    const workPackageFilterResult = workPackageList.filter((item) => item.id === id);
    setFilteredWorkPackageList(workPackageFilterResult);

    const projectFilterResult = projectsList.filter(
      (projectObj) => projectObj.id === workPackageFilterResult[0].projectId,
    );
    setFilteredProjectsList(projectFilterResult);
    setProjectPermanentHover(projectFilterResult[0].id);

    const relatedTaskList = taskList.filter((taskObj) =>
      workPackageFilterResult.some((workPackageObj) => workPackageObj.id === taskObj.workPackageNumberId),
    );
    setFilteredTaskList(relatedTaskList);

    const relatedProjectInputList = projectInputList.filter((projectInputObj) =>
      relatedTaskList.some((taskObj) => taskObj.id === projectInputObj.taskNumberId),
    );
    setFilteredProjectInputList(relatedProjectInputList);

    const relatedKanbanList = kanbanList.filter((actionObj) =>
      relatedTaskList.some((taskObj) => taskObj.id === actionObj.taskNumberId),
    );
    setFilteredKanbanList(relatedKanbanList);

    const relatedDeliverableList = deliverableList.filter((outcomeObj) =>
      relatedTaskList.some((actionObj) => actionObj.id === outcomeObj.taskNumberId),
    );
    setFilteredDeliverableList(relatedDeliverableList);

    const relatedTestList = testList.filter((observationTaskObj) =>
      relatedTaskList.some((outcomeObj) => outcomeObj.id === observationTaskObj.taskNumberId),
    );
    setFilteredTestList(relatedTestList);
  };

  const handleTaskFilter = (id: string) => {
    setDeliverablePermanentHover(null);
    setTestPermanentHover(null);
    setProjectInputPermanentHover(null);

    toggleState.collapsed.project = true;
    toggleState.collapsed.workPackage = true;
    toggleState.collapsed.task = true;
    toggleState.collapsed.kanban = false;

    setTaskPermanentHover(id);

    const taskFilterResult = taskList.filter((item) => item.id === id);
    setFilteredTaskList(taskFilterResult);

    const workPackageFilterResult = workPackageList.filter(
      (workPackageObj) => workPackageObj.id === taskFilterResult[0].workPackageNumberId,
    );
    setFilteredWorkPackageList(workPackageFilterResult);
    setWorkPackagePermanentHover(workPackageFilterResult[0].id);

    const projectFilterResult = projectsList.filter(
      (projectObj) => projectObj.id === workPackageFilterResult[0].projectId,
    );
    setFilteredProjectsList(projectFilterResult);
    setProjectPermanentHover(projectFilterResult[0].id);

    const relatedProjectInputList = projectInputList.filter((projectInputObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === projectInputObj.taskNumberId),
    );
    setFilteredProjectInputList(relatedProjectInputList);

    const relatedKanbanList = kanbanList.filter((actionObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === actionObj.taskNumberId),
    );
    setFilteredKanbanList(relatedKanbanList);

    const relatedDeliverableList = deliverableList.filter((outcomeObj) =>
      taskFilterResult.some((actionObj) => actionObj.id === outcomeObj.taskNumberId),
    );
    setFilteredDeliverableList(relatedDeliverableList);

    const relatedTestList = testList.filter((observationTaskObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === observationTaskObj.taskNumberId),
    );
    setFilteredTestList(relatedTestList);
  };

  const handleProjectInputsFilter = (id: string) => {
    setDeliverablePermanentHover(null);
    setTestPermanentHover(null);

    toggleState.collapsed.project = true;
    toggleState.collapsed.task = false;
    toggleState.collapsed.workPackage = true;

    const injectionFilterResult = projectInputList.filter((item) => item.id === id);
    setFilteredProjectInputList(injectionFilterResult);
    setProjectInputPermanentHover(injectionFilterResult[0].id);

    const taskFilterResult = taskList.filter((taskObj) => taskObj.id === injectionFilterResult[0].taskNumberId);
    setFilteredTaskList(taskFilterResult);
    setTaskPermanentHover(taskFilterResult[0].id);

    const workPackageFilterResult = workPackageList.filter(
      (workPackageObj) => workPackageObj.id === taskFilterResult[0].workPackageNumberId,
    );

    setFilteredWorkPackageList(workPackageFilterResult);

    setWorkPackagePermanentHover(workPackageFilterResult[0].id);

    const relatedProjectList = projectsList.filter(
      (projectObj) => projectObj.id === workPackageFilterResult[0].projectId,
    );
    setFilteredProjectsList(relatedProjectList);
    setProjectPermanentHover(relatedProjectList[0].id);

    const relatedKanbanList = kanbanList.filter((actionObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === actionObj.taskNumberId),
    );
    setFilteredKanbanList(relatedKanbanList);
    if (relatedKanbanList.length === 0) {
      toggleState.collapsed.kanban = true;
    }

    const relatedDeliverableList = deliverableList.filter((outcomeObj) =>
      taskFilterResult.some((actionObj) => actionObj.id === outcomeObj.taskNumberId),
    );

    setFilteredDeliverableList(relatedDeliverableList);

    const relatedTestList = testList.filter((observationTaskObj) =>
      taskFilterResult.some((taskObj) => taskObj.id === observationTaskObj.taskNumberId),
    );
    setFilteredTestList(relatedTestList);
  };

  const handleDeliverableFilter = (id: string) => {
    setTestPermanentHover(null);
    setProjectInputPermanentHover(null);

    toggleState.collapsed.project = true;
    toggleState.collapsed.workPackage = true;
    toggleState.collapsed.task = true;

    const test = testList.find((item) => item.id === id);

    if (test) {
      id = test.deliverableNumberId;
    }

    setDeliverablePermanentHover(id);

    const deliverableFilterResult = deliverableList.filter((item) => item.id === id);
    setFilteredDeliverableList(deliverableFilterResult);

    const taskFilterResult = taskList.filter((taskObj) => taskObj.id === deliverableFilterResult[0].taskNumberId);
    setFilteredTaskList(taskFilterResult);
    setTaskPermanentHover(taskFilterResult[0].id);

    const workPackageFilterResult = workPackageList.filter(
      (workPackageObj) => workPackageObj.id === taskFilterResult[0].workPackageNumberId,
    );
    setFilteredWorkPackageList(workPackageFilterResult);
    setWorkPackagePermanentHover(workPackageFilterResult[0].id);

    const relatedProjectList = projectsList.filter(
      (projectObj) => projectObj.id === workPackageFilterResult[0].projectId,
    );
    setFilteredProjectsList(relatedProjectList);
    setProjectPermanentHover(relatedProjectList[0].id);

    const relatedProjectInputList = projectInputList.filter(
      (projectInputObj) => projectInputObj.id === deliverableFilterResult[0].relatedProjectInput,
    );
    setFilteredProjectInputList(relatedProjectInputList);

    if (relatedProjectInputList && relatedProjectInputList.length > 0) {
      toggleState.collapsed.kanban = true;
    }

    const relatedKanbanList = kanbanList.filter(
      (kanbanObj) => kanbanObj.id === deliverableFilterResult[0].relatedKanban,
    );
    setFilteredKanbanList(relatedKanbanList);

    if (relatedKanbanList && relatedKanbanList.length > 0) {
      toggleState.collapsed.projectInputs = true;
    }

    const relatedTestList = testList.filter((testObj) =>
      deliverableFilterResult.some((deliverableObj) => deliverableObj.id === testObj.deliverableNumberId),
    );

    setFilteredTestList(relatedTestList);
  };

  const handleReset = (columnName: ColumnKey) => {
    switch (columnName) {
      case 'project':
        setFilteredProjectsList(projectsList);
        setFilteredWorkPackageList(workPackageList);
        setFilteredTaskList(taskList);
        setFilteredProjectInputList(projectInputList);
        setFilteredKanbanList(kanbanList);
        setFilteredDeliverableList(deliverableList);
        setFilteredTestList(testList);

        setProjectPermanentHover(null);
        setWorkPackagePermanentHover(null);
        setTaskPermanentHover(null);
        setProjectInputPermanentHover(null);
        setDeliverablePermanentHover(null);
        setTestPermanentHover(null);

        toggleState.collapsed.workPackage = false;
        toggleState.collapsed.task = false;
        toggleState.collapsed.projectInputs = false;
        toggleState.collapsed.kanban = false;
        toggleState.collapsed.deliverable = false;
        toggleState.collapsed.test = false;
        break;

      case 'task':
        setFilteredTaskList(taskList);
        setFilteredProjectInputList(projectInputList);
        setFilteredKanbanList(kanbanList);
        setFilteredDeliverableList(deliverableList);
        setFilteredTestList(testList);

        setTaskPermanentHover(null);
        setProjectInputPermanentHover(null);
        setDeliverablePermanentHover(null);
        setTestPermanentHover(null);

        toggleState.collapsed.projectInputs = false;
        toggleState.collapsed.kanban = false;
        toggleState.collapsed.deliverable = false;
        toggleState.collapsed.test = false;

        break;
      case 'workPackage':
        setFilteredWorkPackageList(workPackageList);
        setFilteredTaskList(taskList);
        setFilteredProjectInputList(projectInputList);
        setFilteredKanbanList(kanbanList);
        setFilteredDeliverableList(deliverableList);
        setFilteredTestList(testList);

        setWorkPackagePermanentHover(null);
        setTaskPermanentHover(null);
        setProjectInputPermanentHover(null);
        setDeliverablePermanentHover(null);
        setTestPermanentHover(null);

        toggleState.collapsed.task = false;
        toggleState.collapsed.projectInputs = false;
        toggleState.collapsed.kanban = false;
        toggleState.collapsed.deliverable = false;
        toggleState.collapsed.test = false;
        break;
      case 'projectInputs':
        setFilteredProjectInputList(projectInputList);
        setFilteredKanbanList(kanbanList);
        setFilteredDeliverableList(deliverableList);
        setFilteredTestList(testList);

        setProjectInputPermanentHover(null);
        setDeliverablePermanentHover(null);
        setTestPermanentHover(null);

        toggleState.collapsed.kanban = false;
        toggleState.collapsed.deliverable = false;
        toggleState.collapsed.test = false;
        break;
      case 'deliverable':
        setFilteredDeliverableList(deliverableList);
        setFilteredTestList(testList);

        setDeliverablePermanentHover(null);
        setTestPermanentHover(null);

        toggleState.collapsed.test = false;
        break;
      case 'test':
        setFilteredTestList(testList);
        setTestPermanentHover(null);
        break;
      default:
        break;
    }
  };

  return (
    <div>
      {setOnClickOpen ? (
        <div>
          <DraggableModal
            headerText={`${t('PPM.kanban.kanbanView')} | ${eventState.eventName}`}
            onClickClose={() => setOnClickOpen(false)}
            style={{
              width: '1600px',
              height: '90vh',
              padding: 24,
            }}
            defaultPosition={{ x: 550, y: -80 }}
          >
            <div style={{ height: '100vh', overflowY: 'hidden' }}>
              <Row style={{ display: 'flex', flexFlow: 'row-wrap', flexWrap: 'nowrap' }} gutter={[12, 12]}>
                {renderCards()}
              </Row>
            </div>
          </DraggableModal>
        </div>
      ) : (
        <div style={{ height: '77vh', overflowY: 'hidden' }}>
          <Row style={{ display: 'flex', flexFlow: 'row-wrap', flexWrap: 'nowrap' }} gutter={[12, 12]}>
            {renderCards()}
          </Row>
        </div>
      )}
    </div>
  );

  function renderCards() {
    return (
      <>
        <Col
          span={toggleState.collapsed.project ? 2 : 6}
          style={{
            transition: 'max-width 0.5s ease',
          }}
        >
          <Card
            title={`${t('PPM.tabs.projects')} (${filteredProjectsList.length})`}
            style={{ background: '#cccccc', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)' }}
            headStyle={{ background: '#252526', fontWeight: 700, borderRadius: 0 }}
            extra={
              <div className="card-extra">
                {filteredProjectsList.length !== projectsList.length && !toggleState.collapsed.project && (
                  <FaFilterCircleXmark onClick={() => handleReset('project')} className="filter-icon" />
                )}

                {toggleState.collapsed.project ? (
                  <BsArrowBarRight onClick={() => toggleCollapse('project')} className="collapse-icon" />
                ) : (
                  <BsArrowBarLeft onClick={() => toggleCollapse('project')} className="collapse-icon" />
                )}
              </div>
            }
          >
            {!toggleState.collapsed.project ? (
              <div
                className={`card-content ${setOnClickOpen ? 'card-content-height-open' : 'card-content-height-closed'}`}
              >
                {filteredProjectsList.map((item) => (
                  <Card
                    key={item.id}
                    className={`kanban-card ${projectPermanentHover === item.id ? 'selected' : ''}`}
                    onClick={() => handleProjectsFilter(item.id)}
                  >
                    <h3 className="kanban-card-body">P-{item.shortName}</h3>
                    <p>{item.longName}</p>
                    {item.lineOfEffortId && lineOfEffortList.length > 0 && (
                      <p>
                        LOE:{' '}
                        {
                          lineOfEffortList.find((lineOfEffort) => lineOfEffort.id === item.lineOfEffortId)
                            ?.lineOfEffortName
                        }
                      </p>
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              <div className={`collapsed-container ${setOnClickOpen ? 'open' : ''}`}>
                <h3 className="collapsed-title">
                  {t('PPM.tabs.projects')} <span className="collapsed-count">({filteredProjectsList.length})</span>
                </h3>
              </div>
            )}
          </Card>
        </Col>

        <Col
          span={toggleState.collapsed.workPackage ? 2 : 6}
          style={{
            transition: 'max-width 0.5s ease',
          }}
        >
          <Card
            title={`${t('PPM.tabs.workPackage')} (${filteredWorkPackageList.length})`}
            style={{ background: '#cccccc', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)' }}
            headStyle={{ background: '#252526', fontWeight: 700, borderRadius: 0 }}
            extra={
              <div className="card-extra">
                {filteredWorkPackageList.length !== workPackageList.length && !toggleState.collapsed.workPackage && (
                  <FaFilterCircleXmark onClick={() => handleReset('workPackage')} className="filter-icon" />
                )}

                {toggleState.collapsed.workPackage ? (
                  <BsArrowBarRight onClick={() => toggleCollapse('workPackage')} className="collapse-icon" />
                ) : (
                  <BsArrowBarLeft onClick={() => toggleCollapse('workPackage')} className="collapse-icon" />
                )}
              </div>
            }
          >
            {!toggleState.collapsed.workPackage ? (
              <div
                className={`card-content ${setOnClickOpen ? 'card-content-height-open' : 'card-content-height-closed'}`}
              >
                {filteredWorkPackageList.map((item) => (
                  <Card
                    key={item.id}
                    className={`kanban-card ${workPackagePermanentHover === item.id ? 'selected' : ''}`}
                    onClick={() => handleWorkPackageFilter(item.id)}
                  >
                    <h3 className="kanban-card-body">W-{item.workPackageNumber}</h3>
                    <p>{item.workPackageName}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <div className={`collapsed-container ${setOnClickOpen ? 'open' : ''}`}>
                <h3 className="collapsed-title">
                  {t('PPM.tabs.projects')} <span className="collapsed-count">({filteredWorkPackageList.length})</span>
                </h3>
              </div>
            )}
          </Card>
        </Col>
        <Col
          span={toggleState.collapsed.task ? 2 : 6}
          style={{
            transition: 'max-width 0.5s ease',
          }}
        >
          <Card
            title={`${t('PPM.tabs.task')} (${filteredTaskList.length})`}
            style={{ background: '#cccccc', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)' }}
            headStyle={{ background: '#252526', fontWeight: 700, borderRadius: 0 }}
            extra={
              <div className="card-extra">
                {filteredTaskList.length !== taskList.length && !toggleState.collapsed.task && (
                  <FaFilterCircleXmark onClick={() => handleReset('task')} className="filter-icon" />
                )}

                {toggleState.collapsed.task ? (
                  <BsArrowBarRight onClick={() => toggleCollapse('task')} className="collapse-icon" />
                ) : (
                  <BsArrowBarLeft onClick={() => toggleCollapse('task')} className="collapse-icon" />
                )}
              </div>
            }
          >
            {!toggleState.collapsed.task ? (
              <div
                className={`card-content ${setOnClickOpen ? 'card-content-height-open' : 'card-content-height-closed'}`}
              >
                {filteredTaskList.map((item) => (
                  <Card
                    key={item.id}
                    className={`kanban-card ${taskPermanentHover === item.id ? 'selected' : ''}`}
                    onClick={() => handleTaskFilter(item.id)}
                  >
                    <h3 className="kanban-card-body">T-{item.taskNumber}</h3>
                    <p>{item.taskName}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <div className={`collapsed-container ${setOnClickOpen ? 'open' : ''}`}>
                <h3 className="collapsed-title">
                  {t('PPM.tabs.projects')} <span className="collapsed-count">({filteredTaskList.length})</span>
                </h3>
              </div>
            )}
          </Card>
        </Col>
        <Col
          span={toggleState.collapsed.projectInputs ? 2 : 6}
          style={{
            transition: 'max-width 0.5s ease',
          }}
        >
          <Card
            title={`${t('PPM.tabs.projectInput')} (${filteredProjectInputList.length})`}
            style={{ background: '#cccccc', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)' }}
            headStyle={{ background: '#252526', fontWeight: 700, borderRadius: 0 }}
            extra={
              <div className="card-extra">
                {filteredProjectInputList.length !== projectInputList.length &&
                  !toggleState.collapsed.projectInputs && (
                    <FaFilterCircleXmark onClick={() => handleReset('projectInputs')} className="filter-icon" />
                  )}

                {toggleState.collapsed.projectInputs ? (
                  <BsArrowBarRight onClick={() => toggleCollapse('projectInputs')} className="collapse-icon" />
                ) : (
                  <BsArrowBarLeft onClick={() => toggleCollapse('projectInputs')} className="collapse-icon" />
                )}
              </div>
            }
          >
            {!toggleState.collapsed.projectInputs ? (
              <div
                className={`card-content ${setOnClickOpen ? 'card-content-height-open' : 'card-content-height-closed'}`}
              >
                {filteredProjectInputList.map((item) => (
                  <Card
                    key={item.id}
                    className={`kanban-card ${projectInputPermanentHover === item.id ? 'selected' : ''}`}
                    onClick={() => handleProjectInputsFilter(item.id)}
                  >
                    <h3 className="kanban-card-body">{item.projectInputNumber}</h3>
                    <p>{item.projectInputName}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <div className={`collapsed-container ${setOnClickOpen ? 'open' : ''}`}>
                <h3 className="collapsed-title">
                  {t('PPM.tabs.projectInput')}{' '}
                  <span className="collapsed-count">({filteredProjectInputList.length})</span>
                </h3>
              </div>
            )}
          </Card>
        </Col>

        <Col
          span={toggleState.collapsed.kanban ? 2 : 6}
          style={{
            transition: 'max-width 0.5s ease',
          }}
        >
          <Card
            title={`${t('PPM.tabs.kanban')} (${filteredKanbanList.length})`}
            style={{ background: '#cccccc', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)' }}
            headStyle={{ background: '#252526', fontWeight: 700, borderRadius: 0 }}
            extra={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => toggleCollapse('kanban')}
              >
                {toggleState.collapsed.kanban ? (
                  <BsArrowBarRight style={{ color: 'white', width: 24, height: 24 }} />
                ) : (
                  <BsArrowBarLeft style={{ color: 'white', width: 24, height: 24 }} />
                )}
              </div>
            }
          >
            {!toggleState.collapsed.kanban ? (
              <div
                className={`card-content ${setOnClickOpen ? 'card-content-height-open' : 'card-content-height-closed'}`}
              >
                {filteredKanbanList.map((item) => (
                  <Card key={item.id} className={`kanban-card-basic`}>
                    <h3 className="kanban-card-body">{item.kanbanNumber}</h3>
                    <p>{item.kanbanName}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <div className={`collapsed-container ${setOnClickOpen ? 'open' : ''}`}>
                <h3 className="collapsed-title">
                  {t('PPM.tabs.kanban')} <span className="collapsed-count">({filteredKanbanList.length})</span>
                </h3>
              </div>
            )}
          </Card>
        </Col>
        <Col
          span={toggleState.collapsed.deliverable ? 2 : 6}
          style={{
            transition: 'max-width 0.5s ease',
          }}
        >
          <Card
            title={`${t('PPM.tabs.deliverable')} (${filteredDeliverableList.length})`}
            style={{ background: '#cccccc', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)' }}
            headStyle={{ background: '#252526', fontWeight: 700, borderRadius: 0 }}
            extra={
              <div className="card-extra">
                {filteredDeliverableList.length !== deliverableList.length && !toggleState.collapsed.deliverable && (
                  <FaFilterCircleXmark onClick={() => handleReset('deliverable')} className="filter-icon" />
                )}

                {toggleState.collapsed.deliverable ? (
                  <BsArrowBarRight onClick={() => toggleCollapse('deliverable')} className="collapse-icon" />
                ) : (
                  <BsArrowBarLeft onClick={() => toggleCollapse('deliverable')} className="collapse-icon" />
                )}
              </div>
            }
          >
            {!toggleState.collapsed.deliverable ? (
              <div
                className={`card-content ${setOnClickOpen ? 'card-content-height-open' : 'card-content-height-closed'}`}
              >
                {filteredDeliverableList.map((item) => (
                  <Card
                    key={item.id}
                    className={`kanban-card ${deliverablePermanentHover === item.id ? 'selected' : ''}`}
                    onClick={() => handleDeliverableFilter(item.id)}
                  >
                    <h3 className="kanban-card-body">{item.deliverableNumber}</h3>
                    <p>{item.deliverableName}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <div className={`collapsed-container ${setOnClickOpen ? 'open' : ''}`}>
                <h3 className="collapsed-title">
                  {t('PPM.tabs.deliverable')}{' '}
                  <span className="collapsed-count">({filteredDeliverableList.length})</span>
                </h3>
              </div>
            )}
          </Card>
        </Col>
        <Col
          span={toggleState.collapsed.test ? 2 : 6}
          style={{
            transition: 'max-width 0.5s ease',
          }}
        >
          <Card
            title={`${t('PPM.tabs.test')} (${filteredTestList.length})`}
            style={{ background: '#cccccc', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)' }}
            headStyle={{ background: '#252526', fontWeight: 700, borderRadius: 0 }}
            extra={
              <div className="card-extra">
                {filteredTestList.length !== testList.length && !toggleState.collapsed.test && (
                  <FaFilterCircleXmark onClick={() => handleReset('test')} className="filter-icon" />
                )}

                {toggleState.collapsed.test ? (
                  <BsArrowBarRight onClick={() => toggleCollapse('test')} className="collapse-icon" />
                ) : (
                  <BsArrowBarLeft onClick={() => toggleCollapse('test')} className="collapse-icon" />
                )}
              </div>
            }
          >
            {!toggleState.collapsed.test ? (
              <div
                className={`card-content ${setOnClickOpen ? 'card-content-height-open' : 'card-content-height-closed'}`}
              >
                {filteredTestList.map((item) => (
                  <Card
                    key={item.id}
                    className={`kanban-card ${testPermanentHover === item.id ? 'selected' : ''}`}
                    onClick={() => handleDeliverableFilter(item.id)}
                  >
                    <h3 className="kanban-card-body">{item.testNumber}</h3>
                    <p>{item.testName}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <div className={`collapsed-container ${setOnClickOpen ? 'open' : ''}`}>
                <h3 className="collapsed-title">
                  {t('PPM.tabs.test')} <span className="collapsed-count">({filteredTestList.length})</span>
                </h3>
              </div>
            )}
          </Card>
        </Col>
      </>
    );
  }
};

export default Kanban;
